const EMPTY_PERCENTAGES = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

const pick = (source, ...keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

const percentagesFromBreakdown = (breakdown) => {
  if (!Array.isArray(breakdown)) return { ...EMPTY_PERCENTAGES };
  const total = breakdown.reduce((sum, row) => sum + (row?.count || 0), 0);
  const out = { ...EMPTY_PERCENTAGES };
  breakdown.forEach((row) => {
    const key = row?.rating;
    if (out[key] === undefined) return;
    out[key] =
      row?.percentage !== undefined && row?.percentage !== null
        ? Math.round(row.percentage)
        : total
          ? Math.round(((row?.count || 0) / total) * 100)
          : 0;
  });
  return out;
};

export const normalizeSiteAggregate = (payload) => {
  const metrics = payload?.metrics || payload || {};
  const subScores = metrics.subScores || {};
  return {
    score: Number(pick(metrics, "average", "score") || 0),
    count: Number(pick(metrics, "totalReviews", "total", "count") || 0),
    percentages: percentagesFromBreakdown(metrics.breakdown),
    subScores: {
      freshness: Number(subScores.freshness || 0),
      delivery: Number(subScores.delivery || 0),
      support: Number(subScores.support || 0),
      recommendation: Number(subScores.recommendation || 0),
    },
  };
};

export const normalizeReview = (review) => {
  if (!review) return null;
  const user = review.user || review.customer || {};
  const anonymous = !!pick(review, "anonymous", "isAnonymous");
  const scores = {
    freshness: pick(review, "freshnessRating", "freshness") ?? null,
    delivery: pick(review, "deliveryRating", "delivery") ?? null,
    support: pick(review, "supportRating", "support") ?? null,
    recommend: pick(review, "recommendationRating", "recommendation") ?? null,
  };
  const answered = Object.values(scores).filter(
    (value) => typeof value === "number" && value > 0,
  );

  return {
    id: pick(review, "id", "_id", "reviewId"),
    anonymous,
    displayName:
      pick(review, "displayName", "name") ?? pick(user, "name", "displayName"),
    firstName: pick(review, "firstName") ?? user.firstName,
    lastName: pick(review, "lastName") ?? user.lastName,
    avatar: pick(user, "profilePicture", "avatar"),
    verifiedOrder: pick(review, "verifiedOrder", "verified") ?? true,
    createdAt: pick(review, "createdAt", "created_at", "date"),
    comment: pick(review, "comment", "review", "text") || "",
    rating:
      Number(pick(review, "rating", "average", "score")) ||
      (answered.length
        ? answered.reduce((a, b) => a + b, 0) / answered.length
        : 0),
    scores,
    items: (pick(review, "items", "orderItems", "products") || []).map(
      (item, index) => ({
        id: pick(item, "productId", "id", "_id") || `item-${index}`,
        name: pick(item, "productName", "name") || "",
        image: pick(item, "productImage", "image"),
      }),
    ),
  };
};

export const normalizeReviewsResponse = (payload) => ({
  aggregate: normalizeSiteAggregate(payload),
  reviews: (payload?.reviews || payload?.data || [])
    .map(normalizeReview)
    .filter(Boolean),
  pagination: payload?.pagination || null,
});

const text = (value) => (typeof value === "string" ? value.trim() : value);

const normalizeOrderProduct = (item, index) => {
  const product = item?.productId;
  return {
    id:
      (typeof product === "string" ? product : pick(product, "id", "_id")) ||
      pick(item, "id", "_id") ||
      `item-${index}`,
    name:
      text(pick(item, "productName", "name")) ||
      text(pick(product, "productName", "name")) ||
      "",
    size: text(pick(item, "size")) || text(pick(product, "size")) || undefined,
    image:
      pick(item, "image", "productImage") ||
      pick(product, "productImage", "image"),
    boxName: text(pick(item, "boxName")) || undefined,
    order: Number(pick(item, "itemOrder") ?? index),
  };
};

const normalizeOrderProducts = (products = []) => {
  const seen = new Set();
  return products
    .map(normalizeOrderProduct)
    .sort((a, b) => a.order - b.order)
    .filter((product) => {
      if (seen.has(product.id)) return false;
      seen.add(product.id);
      return true;
    });
};

export const normalizeReviewableOrder = (order) => {
  if (!order) return null;
  const review = order.review || null;
  const rated = !!pick(order, "rated", "hasReview", "isReviewed") || !!review;
  const items = normalizeOrderProducts(
    pick(order, "products", "items", "cart") || [],
  );
  const id = pick(order, "orderId", "id", "_id");
  const sku = pick(order, "sku", "orderSku");
  const ref = pick(order, "orderRef", "reference") ?? sku;

  return {
    id,
    sku,
    ref: ref ? `#${String(ref).replace(/^#/, "")}` : `#${id || ""}`,
    deliveredAt: pick(order, "deliveredAt", "deliveryDate", "updatedAt"),
    itemCount: Number(pick(order, "itemCount", "totalItems") ?? items.length),
    total: Number(pick(order, "total", "totalAmount", "amount") || 0),
    rated,
    ratedStars: review ? reviewStars(review) : undefined,
    reviewId: review ? pick(review, "id", "_id") : undefined,
    items,
  };
};

export const reviewStars = (review) => normalizeReview(review)?.rating || 0;

export const normalizeReviewableOrders = (payload) =>
  (payload?.orders || payload?.data || (Array.isArray(payload) ? payload : []))
    .map(normalizeReviewableOrder)
    .filter(Boolean);

export const normalizeOrderReviewState = (payload) => {
  if (!payload) return null;
  const source = payload.order || payload.data || payload;
  const review = source.review || payload.review || null;
  const rated =
    !!review || !!pick(source, "rated", "hasReview", "isReviewed", "reviewed");
  const canReview = pick(
    source,
    "canReview",
    "eligible",
    "isReviewable",
    "reviewable",
  );
  return {
    rated,
    canReview: canReview === undefined ? !rated : !!canReview,
    stars: review ? reviewStars(review) : 0,
    reviewId: review ? pick(review, "id", "_id") : undefined,
    deliveredAt: pick(source, "deliveredAt", "deliveryDate"),
  };
};

export const normalizeProductRating = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  const source = payload.metrics || payload.rating || payload;
  const count = Number(
    pick(source, "totalRatings", "totalReviews", "count", "total") || 0,
  );
  if (!count) return null;
  return {
    score: Number(pick(source, "average", "score", "rating") || 0),
    count,
    percentages: percentagesFromBreakdown(source.breakdown),
  };
};

export const productCardRating = (product) => {
  const raw = product?.rating;
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "number") {
    const count = Number(
      pick(product, "ratingCount", "totalRatings", "reviewCount") || 0,
    );
    return count ? { score: raw, count } : null;
  }
  const normalized = normalizeProductRating(raw);
  return normalized ? { score: normalized.score, count: normalized.count } : null;
};

export const buildReviewPayload = ({
  scores,
  supportNA,
  comment,
  anonymous,
  itemRatings,
}) => ({
  freshnessRating: scores?.freshness || 0,
  deliveryRating: scores?.delivery || 0,
  supportRating: supportNA ? null : scores?.support || 0,
  recommendationRating: scores?.recommend || 0,
  comment: comment?.trim() || "",
  anonymous: !!anonymous,
  itemRatings: Object.entries(itemRatings || {})
    .filter(([, rating]) => !!rating)
    .map(([productId, rating]) => ({ productId, rating })),
});

export const normalizeFeaturedReviews = (payload, limit = 5) => {
  const source = payload?.data && !payload?.reviews ? payload.data : payload;
  const aggregate = normalizeSiteAggregate(source);
  const reviews = (source?.reviews || [])
    .map(normalizeReview)
    .filter((review) => review && review.comment.trim())
    .sort(
      (a, b) =>
        new Date(b.createdAt || 0).getTime() -
        new Date(a.createdAt || 0).getTime(),
    )
    .slice(0, limit);
  return { score: aggregate.score, total: aggregate.count, reviews };
};
